import {
    describeJourney,
    formatAuditTime,
    formatPingSummaryLine,
    getPingKey,
} from '_utils/arrival-audit';
import { sanitizeExcelWorkbook } from '_utils/sanitize-spreadsheet-cell';

const NAVY = 'FF1B4F8A';
const ROW_VLT = 'FFEAF4FB';
const BORDER = { style: 'thin', color: { argb: 'FFB7C9DD' } };
const ALL_BORDERS = { top: BORDER, left: BORDER, bottom: BORDER, right: BORDER };

// Map image size in the sheet (px); the row height is set to fit it
const MAP_W = 390;
const MAP_H = 262;

const PAX_COLUMNS = [
    { header: '#', width: 5 },
    { header: 'Passenger Name', width: 26 },
    { header: 'Pax Type', width: 14 },
    { header: 'SSR', width: 12 },
    { header: 'Agent(s)', width: 24 },
    { header: 'Journey', width: 34 },
    { header: 'Confirm', width: 10 },
    { header: 'At Location', width: 11 },
    { header: 'Start', width: 10 },
    { header: 'Finish', width: 10 },
    { header: 'GPS/Beacon Pings', width: 40 },
    { header: 'GPS/Beacon Map', width: 57 },
    { header: 'Comments', width: 50 },
];
const MAP_COL = PAX_COLUMNS.findIndex((c) => c.header === 'GPS/Beacon Map');

const headerCell = (cell, value) => {
    cell.value = value;
    cell.font = { name: 'Arial', bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = ALL_BORDERS;
};

const bodyCell = (cell, value, { fill, align = 'left' } = {}) => {
    cell.value = value;
    cell.font = { name: 'Arial', size: 10, color: { argb: 'FF333333' } };
    cell.alignment = { vertical: 'top', horizontal: align, wrapText: true };
    cell.border = ALL_BORDERS;
    if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
};

const legPingLines = (audit, pingsByKey) =>
    audit.legs
        .map((leg) => {
            const summary = pingsByKey[getPingKey(leg)]?.summary || [];
            const heading = `${leg.type} – ${leg.employeeName}`;
            if (!leg.employeeId || !leg.window) return `${heading}: no allocation window`;
            if (!summary.length) return `${heading}: no pings`;
            return [heading, ...summary.map(formatPingSummaryLine)].join('\n');
        })
        .join('\n\n');

/**
 * Builds and downloads the audit workbook.
 * Sheet 1: flight details + one row per passenger with a map snapshot and a blank Comments column.
 * Sheet 2: every GPS/beacon ping inside each leg's allocated → finished window.
 */
export const downloadArrivalAuditExcel = async ({ flightDetails, audits, pingsByKey, mapImages = {}, fileName }) => {
    const Excel = await import('exceljs/dist/exceljs.min.js');
    const workbook = new Excel.Workbook();
    const sheet = workbook.addWorksheet('Arrival Audit', { views: [{ state: 'frozen', ySplit: 6 }] });

    PAX_COLUMNS.forEach((c, i) => { sheet.getColumn(i + 1).width = c.width; });

    // Title
    sheet.mergeCells(1, 1, 1, PAX_COLUMNS.length);
    const title = sheet.getCell(1, 1);
    title.value = `BFS Arrivals Audit Report – ${flightDetails.flightNumber} – ${flightDetails.date}`;
    title.font = { name: 'Arial', bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
    title.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    title.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    sheet.getRow(1).height = 28;

    // Flight details
    const flightFields = [
        ['Date', flightDetails.date],
        ['Airline', flightDetails.airline],
        ['Flight No', flightDetails.flightNumber],
        ['STA', flightDetails.sta],
        ['ATA', flightDetails.ata],
        ['Origin', flightDetails.origin],
        ['Arrival Gate', flightDetails.gate],
    ];
    flightFields.forEach(([label, value], i) => {
        headerCell(sheet.getCell(3, i + 2), label);
        bodyCell(sheet.getCell(4, i + 2), value || 'N/A', { fill: ROW_VLT, align: 'center' });
    });
    headerCell(sheet.getCell(3, 1), 'Flight');
    bodyCell(sheet.getCell(4, 1), 'Arrival', { fill: ROW_VLT, align: 'center' });
    sheet.getRow(3).height = 20;
    sheet.getRow(4).height = 20;

    // Passenger table
    PAX_COLUMNS.forEach((c, i) => headerCell(sheet.getCell(6, i + 1), c.header));
    sheet.getRow(6).height = 22;

    audits.forEach((audit, idx) => {
        const rowNumber = 7 + idx;
        const fill = idx % 2 === 1 ? ROW_VLT : undefined;
        const values = [
            idx + 1,
            audit.name,
            audit.adviceStatus || 'N/A',
            audit.assistance.join(', ') || 'N/A',
            audit.agents.join('\n') || 'Unassigned',
            describeJourney(audit.legs),
            formatAuditTime(audit.confirm) || 'N/A',
            formatAuditTime(audit.atLocation) || 'N/A',
            formatAuditTime(audit.start) || 'N/A',
            formatAuditTime(audit.finish) || 'Not finished',
            legPingLines(audit, pingsByKey),
            mapImages[audit.key] ? '' : 'No map',
            '',
        ];
        values.forEach((v, i) => {
            const isTime = i >= 6 && i <= 9;
            // Comments stays white so it reads as the column to fill in
            bodyCell(sheet.getCell(rowNumber, i + 1), v, {
                fill: i === PAX_COLUMNS.length - 1 ? undefined : fill,
                align: i === 0 || isTime ? 'center' : 'left',
            });
        });

        const image = mapImages[audit.key];
        if (image) {
            const imageId = workbook.addImage({ base64: image, extension: 'jpeg' });
            sheet.addImage(imageId, {
                tl: { col: MAP_COL + 0.05, row: rowNumber - 1 + 0.05 },
                ext: { width: MAP_W, height: MAP_H },
                editAs: 'oneCell',
            });
        }
        const pingLines = values[10].split('\n').length;
        // px → pt is 0.75; tall enough for the map, or the ping list if that is longer
        sheet.getRow(rowNumber).height = Math.max(image ? MAP_H * 0.75 + 8 : 30, pingLines * 13);
    });

    // Raw pings sheet
    const pingSheet = workbook.addWorksheet('GPS & Beacon Pings', { views: [{ state: 'frozen', ySplit: 1 }] });
    const pingColumns = [
        ['Passenger(s)', 30], ['Agent', 22], ['Leg', 11], ['Window', 16], ['Time', 10], ['Source', 9],
        ['Location', 26], ['Latitude', 12], ['Longitude', 12], ['Accuracy (m)', 12], ['Beacon (major-minor)', 18], ['RSSI', 8],
    ];
    pingColumns.forEach(([label, width], i) => {
        pingSheet.getColumn(i + 1).width = width;
        headerCell(pingSheet.getCell(1, i + 1), label);
    });

    const legsByKey = new Map();
    for (const audit of audits) {
        for (const leg of audit.legs) {
            if (!leg.employeeId || !leg.window) continue;
            const key = getPingKey(leg);
            if (!legsByKey.has(key)) legsByKey.set(key, { leg, passengers: [] });
            legsByKey.get(key).passengers.push(audit.name);
        }
    }

    let r = 2;
    for (const [key, { leg, passengers }] of legsByKey) {
        const pings = pingsByKey[key]?.pings || [];
        const window = `${formatAuditTime(leg.window.start)}–${leg.window.open ? 'open' : formatAuditTime(leg.window.end)}`;
        const base = [passengers.join(', '), leg.employeeName, leg.type, window];
        if (!pings.length) {
            [...base, '', '', 'No pings in window'].forEach((v, i) => bodyCell(pingSheet.getCell(r, i + 1), v));
            r += 1;
            continue;
        }
        for (const p of pings) {
            [
                ...base,
                formatAuditTime(p.ms),
                p.source,
                p.locations.join(', ') || (p.source === 'Beacon' ? 'Unmapped beacon' : 'No mapped area'),
                p.source === 'GPS' ? p.lat : '',
                p.source === 'GPS' ? p.lng : '',
                p.accuracy ?? '',
                p.source === 'Beacon' ? `${p.major}-${p.minor}` : '',
                p.rssi ?? '',
            ].forEach((v, i) => bodyCell(pingSheet.getCell(r, i + 1), v, { align: i >= 4 ? 'center' : 'left' }));
            r += 1;
        }
    }

    sanitizeExcelWorkbook(workbook);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
